// URL-synced tabs. Each tab is a route: the URL is the only source of truth,
// so tabs survive reloads, can be linked to, and work with Back/Forward.
//
//   <Tabs label="Tenant sections" items={[{ to: 'overview', label: 'Overview' }, …]} />
//   <Outlet />   ← the active tab's route renders here
//
// `to` is relative to the current route (React Router semantics), so the
// same component works for /tenants/:id/:tab and /audit/:tab.

import { useEffect, useRef, type KeyboardEvent } from 'react'
import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/format'

export interface TabItem {
  to: string
  label: string
  /** Danger tabs read in the bad colour. */
  tone?: 'default' | 'danger'
  /** Small count after the label. */
  count?: number
}

export interface TabsProps {
  label: string
  items: TabItem[]
  className?: string
}

export function Tabs({ label, items, className }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null)

  // Keep the active tab visible in the horizontal scroller on phones.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[aria-current="page"]')
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  })

  // Left/Right arrow keys move focus between tabs (links stay Tab-reachable).
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    const links = Array.from(listRef.current?.querySelectorAll<HTMLAnchorElement>('a') ?? [])
    const i = links.indexOf(document.activeElement as HTMLAnchorElement)
    if (i === -1) return
    e.preventDefault()
    const next = links[(i + (e.key === 'ArrowRight' ? 1 : links.length - 1)) % links.length]
    next?.focus()
  }

  return (
    <nav aria-label={label} className={cn('relative min-w-0 border-b border-line', className)}>
      <div ref={listRef} onKeyDown={onKeyDown} className="scrollbar-none -mb-px flex gap-0.5 overflow-x-auto [mask-image:linear-gradient(to_right,transparent,black_12px,black_calc(100%-28px),transparent)] md:[mask-image:none]">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            replace
            className={({ isActive }) =>
              cn(
                'shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 text-[13px] font-semibold transition-colors',
                isActive
                  ? cn('border-accent', item.tone === 'danger' ? 'text-bad' : 'text-ink')
                  : cn('border-transparent hover:text-ink', item.tone === 'danger' ? 'text-bad/70' : 'text-muted'),
              )
            }
          >
            {item.label}
            {item.count !== undefined ? <span className="ml-1.5 font-mono text-2xs text-subtle">{item.count}</span> : null}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}
